import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { queryClient } from "@/lib/query-client.ts";
import { unlinkMentions } from "@/shared/memory/content.ts";
import type { MemoryUpdateReq } from "@/types/memory.ts";
import { useMutation } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { updateMemory } from "./api.ts";
import { ConfirmMemoryButton, DeleteMemoryButton, ViewMemoryButton } from "./MemoryItemActions.tsx";
import type { Memory } from "./types.ts";

/**
 * The single editing surface for a memory (opened by tapping the row on any
 * viewport). Plain-text: mention links are flattened to names for editing and
 * the backend re-derives `[Name](urn)` links on save. Delete, Confirm, and the
 * admin graph link live here too.
 */
export const EditMemoryDialog = ({
  memory,
  open,
  onOpenChange,
  isAdmin = false,
}: {
  memory: Memory;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isAdmin?: boolean;
}) => {
  const [content, setContent] = useState(() => unlinkMentions(memory.content || ""));
  const [isDirty, setIsDirty] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { mutate: save, isPending } = useMutation({
    mutationFn: (data: MemoryUpdateReq) => updateMemory(memory.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["memories"] });
      onOpenChange(false);
    },
    onError: () => {
      errorToast("Failed to update memory");
    },
  });

  // Focus the field once the dialog has opened.
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [open]);

  // If the memory refreshes from the server while the dialog is open and the
  // user hasn't started editing, adopt the newer content so a save doesn't
  // clobber it with the stale initial value.
  useEffect(() => {
    if (!isDirty) {
      setContent(unlinkMentions(memory.content || ""));
    }
  }, [memory.content, isDirty]);

  const handleContentChange = (next: string) => {
    setContent(next);
    setIsDirty(true);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit memory</DialogTitle>
        </DialogHeader>
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => handleContentChange(e.target.value)}
          rows={5}
          className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label="Edit memory content"
        />
        <DialogFooter className="mt-2 flex-row items-center justify-end">
          {isAdmin && <ViewMemoryButton memoryId={memory.id} />}
          {!memory.confirmed && <ConfirmMemoryButton memory={memory} />}
          <DeleteMemoryButton memory={memory} onDeleted={() => onOpenChange(false)} />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onOpenChange(false)}
            data-statsig-name="memories-edit-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => save({ content })}
            disabled={isPending}
            data-statsig-name="memories-edit-save"
          >
            <Save className="size-4" />
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
